import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Name the next action. "No data" alone leaves the user stuck. */
  action?: ReactNode;
  className?: string;
}

/**
 * Distinguish the three empties, they need different copy and different
 * actions:
 *   1. nothing exists yet  → offer creation
 *   2. a filter matched nothing → offer clearing the filter
 *   3. the fetch failed → that is an <Alert>, not an EmptyState
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        // Bracket values, not `min-h-50`/`bg-…/2` — those rely on v4's dynamic
        // spacing and opacity scales and generate nothing under v3.
        'flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 bg-neutral-900/[0.02] p-6 text-center',
        className,
      )}
    >
      {icon && <div className="text-neutral-400">{icon}</div>}
      <p className="text-sm font-medium text-neutral-800">{title}</p>
      {description && <p className="max-w-sm text-xs text-neutral-500">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
